import { objectKeys } from "@common/helpers/objects";
import { pluralizeWithCount } from "@common/helpers/string";
import type { BoardMoveBatch } from "@design-system/Board/Board.types";
import { describePartialDrop } from "@design-system/Board/boardGroupAnnouncements";
import { formatDateOnly } from "@helpers/date";
import type { DemoTask } from "./fixtures/demoTasks";
import {
    NO_VALUE_ID,
    setAxisValue,
    TASK_AXIS_VALUES,
    type TaskAxis,
} from "./taskBoardAxes";

/** A task field the board's actions set from a list of values. */
export type TaskActionField = Extract<
    TaskAxis,
    "assignee" | "status" | "priority"
>;

/**
 * One edit from the board's action set (Assign, Status, Priority, Due
 * date), shared by the command bar, card chips and shortcuts, plus the
 * card-only Rename and Entity edits. List fields take a `TASK_AXIS_VALUES`
 * id, `NO_VALUE_ID` clearing the field.
 */
export type TaskBoardChange =
    | { field: TaskActionField; valueId: string }
    | { field: "dueDate"; dueDateOnly: string | null }
    | { field: "title"; title: string }
    | { field: "entities"; entityIds: readonly number[] };

/** A field the action set edits; also its card field id, where it has one. */
export type TaskBoardField = TaskBoardChange["field"];

// A Record, so a new field can't be left out; keys in card order.
const CARD_FIELDS: Record<TaskBoardField, true> = {
    title: true,
    status: true,
    entities: true,
    dueDate: true,
    priority: true,
    assignee: true,
};

/** Every field a card can edit inline, in card order. */
export const TASK_BOARD_FIELDS: readonly TaskBoardField[] =
    objectKeys(CARD_FIELDS);

/** Narrows a card field id to the field a card can edit, if it can. */
export const toTaskBoardField = (id: string) =>
    TASK_BOARD_FIELDS.find((field) => field === id);

/** Each action's name, on the command bar and in the board's keyboard help. */
export const TASK_ACTION_LABELS: Record<TaskBoardField, string> = {
    assignee: "Assign",
    status: "Status",
    priority: "Priority",
    dueDate: "Due date",
    title: "Rename",
    entities: "Entity",
};

const applyToTask = (task: DemoTask, change: TaskBoardChange): DemoTask => {
    switch (change.field) {
        case "dueDate":
            return { ...task, dueDateOnly: change.dueDateOnly };
        case "title":
            return { ...task, title: change.title };
        case "entities":
            return { ...task, entityIds: change.entityIds };
        default:
            return setAxisValue(task, change.field, {
                fromId: null,
                toId: change.valueId,
            });
    }
};

interface ApplyTaskChangeArgs {
    tasks: readonly DemoTask[];
    taskIds: ReadonlySet<string>;
    change: TaskBoardChange;
}

/** `tasks` with `change` applied to the ones in `taskIds`; the rest stay as they are. */
export const applyTaskChange = ({
    tasks,
    taskIds,
    change,
}: ApplyTaskChangeArgs): DemoTask[] =>
    tasks.map((task) =>
        taskIds.has(task.id) ? applyToTask(task, change) : task,
    );

const getValueTitle = (field: TaskActionField, valueId: string) =>
    TASK_AXIS_VALUES[field].find(({ id }) => id === valueId)?.title ?? "";

/**
 * The toast after a group drop left some tasks behind: "Moved 3 tasks, 1
 * couldn't move. Only done tasks can move to Deliver.", or "1 task couldn't
 * move. …" when none moved. Each distinct reason once, in board order.
 */
export const describePartialMove = ({
    moves,
    refusals,
}: BoardMoveBatch<DemoTask>) =>
    describePartialDrop({ noun: "task", movedCount: moves.length, refusals });

/** The toast after `change` reached `count` tasks ("Assigned 3 tasks to Avery Chen"). */
export const describeTaskChange = (change: TaskBoardChange, count: number) => {
    const tasks = pluralizeWithCount("task", count);
    if (change.field === "dueDate") {
        return change.dueDateOnly === null
            ? `Cleared due date for ${tasks}`
            : `Updated due date to ${formatDateOnly(change.dueDateOnly)} for ${tasks}`;
    }
    if (change.field === "title") {
        return `Renamed ${tasks} to "${change.title}"`;
    }
    if (change.field === "entities") {
        return `Updated entities for ${tasks}`;
    }
    const title = getValueTitle(change.field, change.valueId);
    const isCleared = change.valueId === NO_VALUE_ID;
    switch (change.field) {
        case "assignee":
            return isCleared
                ? `Removed assignee from ${tasks}`
                : `Assigned ${tasks} to ${title}`;
        case "status":
            return `Updated status to ${title} for ${tasks}`;
        case "priority":
            return isCleared
                ? `Cleared priority for ${tasks}`
                : `Updated priority to ${title} for ${tasks}`;
    }
};
